import { useEffect } from "react";

/**
 * Hold a screen wake lock while the kiosk player runtime is active.
 *
 * Why: a backgrounded/throttled/frozen player tab keeps answering
 * protocol-level WebSocket pongs (the browser does that off the main thread),
 * so the gateway still counts the device as connected — while its JS event
 * loop never runs, commands queue unexecuted, and the operator sees
 * "delivered" with nothing happening. Keeping the screen awake keeps the tab
 * foregrounded and its event loop live, closing the biggest zombie-player
 * window on kiosk hardware.
 *
 * The lock is auto-released by the browser whenever the page loses
 * visibility or fullscreen changes, so we re-request it on those events.
 * Silently no-ops where the Wake Lock API is unsupported or denied
 * (playback itself is unaffected).
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const wakeLock = navigator.wakeLock;
    if (!wakeLock?.request) return;

    let disposed = false;
    let sentinel: WakeLockSentinel | null = null;

    const acquire = async () => {
      if (disposed || document.visibilityState !== "visible") return;
      try {
        const lock = await wakeLock.request("screen");
        if (disposed) {
          void lock.release().catch(() => {});
          return;
        }
        sentinel = lock;
      } catch {
        // Denied (battery saver, permissions policy) — playback continues,
        // we just lose the anti-doze assist.
      }
    };

    const reacquire = () => {
      // The browser released the lock (tab hidden, fullscreen toggle);
      // request it again as soon as we are visible.
      void acquire();
    };

    document.addEventListener("visibilitychange", reacquire);
    document.addEventListener("fullscreenchange", reacquire);
    void acquire();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", reacquire);
      document.removeEventListener("fullscreenchange", reacquire);
      void sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [active]);
}
